import React, {useMemo} from 'react'
import {Box, Grid, InputAdornment, Stack, TextField, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import NotItems from '@ui/notItems/NotItems'
import {usePaginationCustom} from '@hooks/pagination/usePaginationCustom'
import PaginationCustom from '@ui/paginationCustom/PaginationCustom'
import {useQueryState} from '@hooks/query/useQueryState'
import SelectPerPage from '@ui/select/base/selectPerPage/SelectPerPage'
import styles from '@containers/monitoringPlatform/photoFixation/list/sx'
import {IAccountCameraSnapshot} from '@containers/monitoringPlatform/photoFixation/types'
import {PhotoFixationCard} from '@containers/monitoringPlatform/photoFixation/list/PhotoFixationCard'

interface IProps {
	snapshots: IAccountCameraSnapshot[]
	accountId: number
	isStatsLoading: boolean
	isPreviewsLoading: boolean
}

const PER_PAGES: {value: string; label: string}[] = [
	{label: '8', value: '8'},
	{label: '12', value: '12'},
	{label: '24', value: '24'},
	{label: 'Всі', value: '0'},
]

export const PhotoFixationList = ({snapshots, accountId, isStatsLoading, isPreviewsLoading}: IProps) => {
	const [page, setPage] = useQueryState<number>('pfPage', 1, {
		parse: (value) => Number(value)
	})
	const [searchValue, setSearchValue] = useQueryState<string>('pfSearch', '')
	const [perPage, setPerPage] = useQueryState<number | null>('pfPerPage', 8, {
		parse: (value) => Number(value) || null
	})

	const targetSnapshots: IAccountCameraSnapshot[] = useMemo(() => {
		if (!snapshots?.length) {
			return []
		}
		if (!searchValue?.length) {
			return snapshots
		}
		const value = searchValue.trim().toLowerCase()
		return snapshots.filter((snapshot) => {
			return snapshot.licencePlate?.toLowerCase().includes(value) ||
				snapshot.deviceId?.toString().toLowerCase().includes(value) ||
				snapshot.hostName?.toLowerCase().includes(value) ||
				snapshot.cameras?.some((camera) => {
					return camera.cameraId?.toLowerCase().includes(value)
				})
		})
	}, [snapshots, searchValue])

	const {totalPage, paginationData} = usePaginationCustom({
		data: targetSnapshots,
		page,
		perPage
	})

	return (
		<Box sx={styles.container}>
			<Box sx={styles.header}>
				<TextField
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					placeholder="Пошук за номером, ID або камерою..."
					size="small"
					sx={styles.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small"/>
							</InputAdornment>
						),
					}}
				/>

				<Stack
					direction="row"
					justifyContent="space-between"
					gap={2}
					alignItems="center"
				>
					<SelectPerPage
						perPage={perPage}
						setPage={setPage}
						setPerPage={setPerPage}
						data={PER_PAGES}
						width="120px"
					/>
					<Typography
						sx={{minWidth: '120px'}}
						variant="body2"
						color="text.secondary"
					>
						<span style={{fontWeight: 700}}>
							{paginationData?.length || 0} / {snapshots?.length || 0}
						</span> пристроїв
					</Typography>
				</Stack>
			</Box>

			{!paginationData?.length && <NotItems/>}

			<Grid container spacing={2}>
				{paginationData?.length >= 1 && paginationData.map((snapshot) => (
					<Grid item xs={12} sm={6} md={4} lg={3} key={snapshot.deviceId}>
						<PhotoFixationCard
							snapshot={snapshot}
							accountId={accountId}
							isStatsLoading={isStatsLoading}
							isPreviewsLoading={isPreviewsLoading}
						/>
					</Grid>
				))}
			</Grid>

			{totalPage > 1 && (
				<PaginationCustom
					onChange={setPage}
					page={page}
					totalPage={totalPage}
					sx={{marginTop: '20px'}}
				/>
			)}
		</Box>
	)
}
